'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Users, Building2, TrendingUp, DollarSign, Search, MoreHorizontal, Plus, Mail, Phone, CheckCircle2, Clock, AlertCircle, XCircle,  } from 'lucide-react';

interface Client {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  status: 'active' | 'inactive' | 'onboarding' | 'churned';
  tier: 'enterprise' | 'business' | 'starter';
  services: string[];
  mrr: number;
  since: string;
  avatar: string;
  manager: string;
  lastActivity: string;
}

const clients: Client[] = [
  { id: 'c1', name: 'Marcus Webb', company: 'Apex Digital', email: 'marcus@apexdigital.ae', phone: '+971 56 321 0987', status: 'active', tier: 'enterprise', services: ['Digital Marketing', 'Web Dev'], mrr: 9580, since: 'Jan 2023', avatar: 'MW', manager: 'Ryan A.', lastActivity: '2h ago' },
  { id: 'c2', name: 'James Okafor', company: 'Meridian BPO', email: 'james@meridianbpo.com', phone: '+971 55 987 6543', status: 'active', tier: 'enterprise', services: ['BPO Center', 'VOIP'], mrr: 14200, since: 'Mar 2022', avatar: 'JO', manager: 'Layla K.', lastActivity: '1d ago' },
  { id: 'c3', name: 'Priya Sharma', company: 'CloudBridge Inc.', email: 'priya@cloudbridge.co', phone: '+971 52 456 7890', status: 'active', tier: 'business', services: ['VOIP', 'IT Support'], mrr: 3400, since: 'Aug 2023', avatar: 'PS', manager: 'Ryan A.', lastActivity: '3d ago' },
  { id: 'c4', name: 'Fatima Al-Rashid', company: 'Gulf Retail Group', email: 'fatima@gulfretail.ae', phone: '+971 54 654 3210', status: 'onboarding', tier: 'business', services: ['Web Dev'], mrr: 2100, since: 'Jul 2024', avatar: 'FA', manager: 'Omar S.', lastActivity: '1h ago' },
  { id: 'c5', name: 'Aisha Noor', company: 'Pinnacle Logistics', email: 'aisha@pinnacle.ae', phone: '+971 55 234 5678', status: 'active', tier: 'business', services: ['BPO Center'], mrr: 4800, since: 'Nov 2022', avatar: 'AN', manager: 'Omar S.', lastActivity: '6h ago' },
  { id: 'c6', name: 'Tom Hargreaves', company: 'Vantage Media', email: 'tom@vantage.media', phone: '+971 52 876 5432', status: 'active', tier: 'starter', services: ['Digital Marketing'], mrr: 1200, since: 'Feb 2024', avatar: 'TH', manager: 'Layla K.', lastActivity: '2d ago' },
  { id: 'c7', name: 'Sarah Mitchell', company: 'NovaTech Solutions', email: 'sarah@novatech.io', phone: '+971 50 123 4567', status: 'inactive', tier: 'starter', services: ['IT Support'], mrr: 0, since: 'Jun 2023', avatar: 'SM', manager: 'Ryan A.', lastActivity: '3w ago' },
  { id: 'c8', name: 'David Chen', company: 'Synapse Analytics', email: 'david@synapse.io', phone: '+971 50 789 0123', status: 'churned', tier: 'business', services: ['IT Support', 'VOIP'], mrr: 0, since: 'Apr 2022', avatar: 'DC', manager: 'Ryan A.', lastActivity: '2mo ago' },
];

const statusConfig = {
  active:     { label: 'Active',      color: 'text-success',  bg: 'bg-success/10',  icon: CheckCircle2 },
  inactive:   { label: 'Inactive',    color: 'text-warning',  bg: 'bg-warning/10',  icon: Clock },
  onboarding: { label: 'Onboarding',  color: 'text-primary',  bg: 'bg-primary/10',  icon: AlertCircle },
  churned:    { label: 'Churned',     color: 'text-danger',   bg: 'bg-danger/10',   icon: XCircle },
};

const tierConfig = {
  enterprise: { label: 'Enterprise', color: 'text-amber-400', bg: 'bg-amber-400/10' },
  business:   { label: 'Business',   color: 'text-primary',   bg: 'bg-primary/10' },
  starter:    { label: 'Starter',    color: 'text-muted-foreground', bg: 'bg-muted' },
};

export default function ClientsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tierFilter, setTierFilter] = useState<string>('all');

  const filtered = clients.filter((c) => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.company.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchTier = tierFilter === 'all' || c.tier === tierFilter;
    return matchSearch && matchStatus && matchTier;
  });

  const totalMRR = clients.filter(c => c.status === 'active' || c.status === 'onboarding').reduce((s, c) => s + c.mrr, 0);
  const activeCount = clients.filter(c => c.status === 'active').length;
  const onboardingCount = clients.filter(c => c.status === 'onboarding').length;
  const churnRate = Math.round((clients.filter(c => c.status === 'churned').length / clients.length) * 100);

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">Clients</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Manage client accounts, contracts, and relationships</p>
          </div>
          <button className="flex items-center gap-2 bg-primary text-primary-foreground px-3 sm:px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors flex-shrink-0">
            <Plus size={16} />
            <span className="hidden sm:inline">Add Client</span>
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[
            { label: 'Total Clients', value: clients.length, sub: `${activeCount} active`, icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
            { label: 'Monthly Revenue', value: `$${(totalMRR / 1000).toFixed(1)}k`, sub: 'MRR across active clients', icon: DollarSign, color: 'text-success', bg: 'bg-success/10' },
            { label: 'Onboarding', value: onboardingCount, sub: 'New clients this quarter', icon: TrendingUp, color: 'text-amber-400', bg: 'bg-amber-400/10' },
            { label: 'Churn Rate', value: `${churnRate}%`, sub: 'Last 12 months', icon: Building2, color: 'text-danger', bg: 'bg-danger/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-card border border-border rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className="text-xs sm:text-sm text-muted-foreground">{kpi.label}</span>
                <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                  <kpi.icon size={16} className={kpi.color} />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-foreground">{kpi.value}</p>
              <p className="text-xs text-muted-foreground mt-1 hidden sm:block">{kpi.sub}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-xl p-3 sm:p-4 mb-4">
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {(['all', 'active', 'onboarding', 'inactive', 'churned'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground' : 'bg-background border border-border text-muted-foreground hover:text-foreground'}`}
                >
                  {s === 'all' ? 'All Status' : s}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {(['all', 'enterprise', 'business', 'starter'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setTierFilter(t)}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${tierFilter === t ? 'bg-primary text-primary-foreground' : 'bg-background border border-border text-muted-foreground hover:text-foreground'}`}
                >
                  {t === 'all' ? 'All Tiers' : t}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Services</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">MRR</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Manager</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last Activity</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((c) => {
                  const sc = statusConfig[c.status];
                  const tc = tierConfig[c.tier];
                  const StatusIcon = sc.icon;
                  return (
                    <tr key={c.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-primary">{c.avatar}</span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{c.name}</p>
                            <p className="text-xs text-muted-foreground">{c.company}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                          <StatusIcon size={11} />{sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${tc.bg} ${tc.color}`}>{tc.label}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.services.map((s) => (
                            <span key={s} className="text-2xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">{s}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-semibold text-foreground">{c.mrr > 0 ? `$${c.mrr.toLocaleString()}` : '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{c.manager}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{c.lastActivity}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><Mail size={14} /></button>
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><Phone size={14} /></button>
                          <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"><MoreHorizontal size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-16 text-center text-muted-foreground text-sm">No clients match your search.</div>
            )}
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden flex flex-col gap-3">
          {filtered.map((c) => {
            const sc = statusConfig[c.status];
            const tc = tierConfig[c.tier];
            const StatusIcon = sc.icon;
            return (
              <div key={c.id} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary">{c.avatar}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground text-sm">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.company}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"><Mail size={14} /></button>
                    <button className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground"><MoreHorizontal size={14} /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.color}`}>
                    <StatusIcon size={10} />{sc.label}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tc.bg} ${tc.color}`}>{tc.label}</span>
                  {c.services.map((s) => (
                    <span key={s} className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">{s}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>MRR: <span className="font-semibold text-foreground">{c.mrr > 0 ? `$${c.mrr.toLocaleString()}` : '—'}</span></span>
                  <span>Mgr: {c.manager}</span>
                  <span>{c.lastActivity}</span>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-16 text-center text-muted-foreground text-sm">No clients match your search.</div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
